/**
 * Station workbench (garment F0, Lens B) — the supervisor's DETAILED view of one
 * station: every in-flight order's received → output → loss → balance and
 * ex-factory, with a clean inline record entry per order. The informative
 * complement to Station Mode (the tap-first kiosk for fast floor recording).
 *
 * Reuses `wip.queue` (the cross-order queue) + `wip.forStyle` (per-size balances)
 * + `wip.record` (the write). No backend change.
 */
import { Fragment, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, ChevronDown, Check, AlertTriangle, Tablet } from "lucide-react";
import { Alert, Button, Input, cn, toast } from "@flint/ui";
import type { wip as wipContract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.js";
import { formatDate } from "../../../lib/format.js";
import { stationByKey } from "../../../lib/garment-stations.js";

type WipStage = wipContract.WipStage;
type WipKind = "out" | "loss";

const LOSS_REASONS = [
  "Fabric defect",
  "Machine breakdown",
  "Measurement reject",
  "Stitching defect",
  "Stain / wash issue",
  "Other",
];

export function FloorWorkbenchPage({ stage }: { stage: string }) {
  const station = stationByKey(stage);
  const query = trpc.wip.queue.useQuery(
    { stage: (station?.key ?? "cutting") as WipStage },
    { enabled: !!station },
  );
  const [openId, setOpenId] = useState<string | null>(null);

  if (!station) {
    return (
      <div className="p-6">
        <Alert intent="bad" title="Unknown station" description={`"${stage}" is not a floor station.`} />
      </div>
    );
  }

  const rows = query.data?.rows ?? [];

  return (
    <div className="mx-auto w-full max-w-[1000px] p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to="/production/floor"
          search={{ view: "station" }}
          aria-label="Back to production"
          className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-line text-muted transition hover:bg-raised hover:text-ink"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-sky-50 text-sky-700">
          <station.icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-bold text-ink">{station.label}</h1>
          <p className="text-sm text-muted">Every order at this station — received, output, loss and balance.</p>
        </div>
        <Link
          to="/station"
          className="hidden shrink-0 items-center gap-1.5 rounded-lg border border-line px-3 py-2 text-sm font-semibold text-ink-2 transition hover:bg-raised sm:flex"
        >
          <Tablet className="h-4 w-4" /> Station Mode
        </Link>
      </div>

      {/* Detail table */}
      <div className="mt-5 overflow-hidden rounded-xl border border-line bg-paper shadow-sm">
        {query.isLoading ? (
          <p className="p-8 text-center text-sm text-muted">Loading the queue…</p>
        ) : query.error ? (
          <div className="p-4">
            <Alert intent="bad" title="Couldn't load the queue" description={query.error.message} />
          </div>
        ) : rows.length === 0 ? (
          <p className="p-10 text-center text-sm text-muted">Nothing at this station right now.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse">
              <thead>
                <tr className="border-b border-line text-xs font-semibold uppercase tracking-[0.08em] text-muted">
                  <th className="py-2.5 pl-5 pr-3 text-left">Order</th>
                  <th className="px-3 py-2.5 text-right">Received</th>
                  <th className="px-3 py-2.5 text-right">Output</th>
                  <th className="px-3 py-2.5 text-right">Loss</th>
                  <th className="px-3 py-2.5 text-right">Balance</th>
                  <th className="px-3 py-2.5 text-left">Ex-factory</th>
                  <th className="py-2.5 pl-3 pr-5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const open = openId === r.styleId;
                  return (
                    <Fragment key={r.styleId}>
                      <tr className={cn("border-b border-line", open ? "bg-raised" : "hover:bg-raised")}>
                        <td className="py-3 pl-5 pr-3">
                          <Link
                            to="/production/styles/$styleId/production"
                            params={{ styleId: r.styleId }}
                            className="font-mono text-base font-semibold text-ink hover:text-sky-700"
                          >
                            {r.styleRef}
                          </Link>
                          <div className="text-xs text-muted">{r.name} · {r.buyerName}</div>
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-base tabular-nums text-ink-2">
                          {r.received.toLocaleString("en-IN")}
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-base tabular-nums text-ink-2">
                          {r.output.toLocaleString("en-IN")}
                        </td>
                        <td className={cn("px-3 py-3 text-right font-mono text-base tabular-nums", r.loss > 0 ? "text-bad" : "text-muted")}>
                          {r.loss > 0 ? r.loss.toLocaleString("en-IN") : "—"}
                        </td>
                        <td className={cn("px-3 py-3 text-right font-mono text-lg font-bold tabular-nums", r.balance > 0 ? "text-warn" : "text-ink")}>
                          {r.balance.toLocaleString("en-IN")}
                        </td>
                        <td className="px-3 py-3 text-sm text-ink-2">
                          {r.exFactoryDate ? formatDate(r.exFactoryDate) : "—"}
                        </td>
                        <td className="py-3 pl-3 pr-5 text-right">
                          <button
                            type="button"
                            onClick={() => setOpenId(open ? null : r.styleId)}
                            aria-expanded={open}
                            className={cn(
                              "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-semibold transition",
                              open ? "border-sky-600 bg-sky-50 text-sky-700" : "border-line text-ink-2 hover:bg-raised",
                            )}
                          >
                            Record
                            <ChevronDown className={cn("h-4 w-4 transition", open && "rotate-180")} />
                          </button>
                        </td>
                      </tr>
                      {open ? (
                        <tr className="border-b border-line bg-raised">
                          <td colSpan={7} className="px-5 py-4">
                            <StationEntry styleId={r.styleId} stage={station.key} onDone={() => setOpenId(null)} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Clean inline record entry for one order — the "old design, better": tap a size,
 * choose output vs loss, type the count, record. Reuses `wip.forStyle` for
 * per-size balances at this stage and `wip.record` for the write.
 */
function StationEntry({ styleId, stage, onDone }: { styleId: string; stage: WipStage; onDone: () => void }) {
  const utils = trpc.useUtils();
  const q = trpc.wip.forStyle.useQuery({ styleId });
  const [size, setSize] = useState<string | null>(null);
  const [kind, setKind] = useState<WipKind>("out");
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState<string | null>(null);

  const record = trpc.wip.record.useMutation({
    onSuccess: async () => {
      setQty("");
      // Invalidate the whole `wip.*` sub-tree: one movement ripples into every
      // downstream stage row (self-linked received), the Grid lens, the
      // finished-goods board (when it's QC output), and per-style balances.
      // Also `styles` (status may flip pp_approved → in_production) and audit.
      await Promise.all([
        utils.wip.invalidate(),
        utils.styles.get.invalidate({ id: styleId }),
        utils.styles.list.invalidate(),
        utils.auditLog.list.invalidate(),
      ]);
      toast.success("Recorded");
      onDone();
    },
    onError: (e: { message: string }) => toast.error("Couldn't record", { description: e.message }),
  });

  if (q.isLoading) return <p className="text-sm text-muted">Loading sizes…</p>;
  const data = q.data;
  if (!data) return <p className="text-sm text-muted">No data.</p>;

  const stageRow = data.stages.find((s) => s.stage === stage);
  const sizeRows =
    stageRow?.sizes ?? data.sizes.map((s) => ({ size: s, received: 0, output: 0, loss: 0, balance: 0 }));
  const activeBalance = sizeRows.find((s) => s.size === size)?.balance ?? 0;
  const loss = kind === "loss";
  const n = Math.round(Number(qty)) || 0;
  const canSubmit = !!size && n > 0 && (!loss || !!reason) && !record.isPending;

  return (
    <div className="flex flex-col gap-3.5">
      {/* Output vs loss */}
      <div className="inline-flex w-fit rounded-lg border border-line bg-paper p-0.5">
        {(["out", "loss"] as const).map((k) => {
          const active = kind === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => {
                setKind(k);
                setReason(null);
              }}
              className={cn(
                "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition",
                active
                  ? k === "loss"
                    ? "bg-bad-50 text-bad"
                    : "bg-sky-50 text-sky-700"
                  : "text-muted hover:text-ink",
              )}
            >
              {k === "loss" ? <AlertTriangle className="h-3.5 w-3.5" /> : null}
              {k === "loss" ? "Loss / reject" : "Finished"}
            </button>
          );
        })}
      </div>

      {/* Size chips with per-size balance */}
      <div className="flex flex-wrap gap-2">
        {sizeRows.map((s) => {
          const selected = s.size === size;
          const pending = s.balance > 0;
          return (
            <button
              key={s.size}
              type="button"
              onClick={() => setSize(s.size)}
              className={cn(
                "flex items-baseline gap-1.5 rounded-lg border px-3 py-2 transition",
                selected ? "border-sky-600 bg-sky-50" : "border-line bg-paper hover:border-line-2",
              )}
            >
              <span className={cn("text-sm font-bold", selected ? "text-sky-700" : "text-ink")}>{s.size}</span>
              <span className={cn("font-mono text-xs tabular-nums", pending ? "text-warn" : "text-muted")}>
                {pending ? s.balance.toLocaleString("en-IN") : "done"}
              </span>
            </button>
          );
        })}
      </div>

      {/* Reason (loss only) */}
      {loss ? (
        <div className="flex flex-wrap gap-2">
          {LOSS_REASONS.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setReason(r)}
              className={cn(
                "rounded-full border px-3 py-1.5 text-sm font-medium transition",
                reason === r ? "border-bad bg-bad-50 text-bad" : "border-line bg-paper text-ink-2 hover:border-line-2",
              )}
            >
              {r}
            </button>
          ))}
        </div>
      ) : null}

      {/* Qty + submit */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Input
            value={qty}
            onChange={(e) => setQty(e.currentTarget.value)}
            placeholder="0"
            inputMode="numeric"
            aria-label="Quantity"
            className="h-12 w-28 text-center font-mono text-xl font-bold tabular-nums"
          />
          <span className="text-sm text-muted">
            pcs{size ? ` · ${activeBalance.toLocaleString("en-IN")} left` : ""}
          </span>
        </div>
        <Button
          intent={loss ? "danger" : "primary"}
          disabled={!canSubmit}
          onClick={() => {
            if (!size || n <= 0) return;
            record.mutate({
              styleId,
              stage,
              size,
              kind,
              qty: n,
              reason: loss ? reason : null,
              entryDate: new Date().toISOString().slice(0, 10),
            });
          }}
          className="h-12 px-6 text-base font-semibold"
        >
          {loss ? <AlertTriangle className="h-4 w-4" /> : <Check className="h-4 w-4" />}
          {loss ? "Log loss" : "Record"}
        </Button>
      </div>
    </div>
  );
}
